/**
 * `task.changed` → repaint (`specs/M10-tasks.md` §6).
 *
 * The api emits **one event per affected user** — the assignee and the assigner — with `userId`
 * set and `boardId` **null**, so it routes to `user:<id>` and never into a board room. The
 * payload is `{ itemId, boardId }` and nothing else: what the recipient may actually see is
 * re-decided by `tasks.list`, not by room membership. So there is nothing to patch here; the
 * event is a *hint to re-read*, and that is all this hook turns it into.
 *
 * The socket is lazy (it opens on the first listener), so this only runs for a signed-in shell.
 */
import { useEffect, useState } from 'react';
import type { EventEnvelope } from '@elizmonday/shared';
import { joinBoard, leaveBoard, onEvent } from '../../lib/realtime/socket';

export const TASK_CHANGED_EVENT = 'task.changed';

export function isTaskEvent(event: Pick<EventEnvelope, 'type'>): boolean {
  return event.type === TASK_CHANGED_EVENT;
}

/**
 * **P-319** · the events that change a *row* without changing a *task*.
 *
 * `task.changed` fires when one of the three mapped columns is written (`onCellWritten`), which
 * covers every edit the list's own inline editors make. It does **not** fire for a rename or a
 * delete — `task_assignments` stores no name, `tasks.list` joins `items.name` live — so an item
 * renamed in the card that is now open over this page left the row underneath showing the old
 * name until something else made the page re-read. These three are that something.
 *
 * They are board-room events, which is the second half: the Tasks page joins no board of its
 * own, so until a card is open there is no room for them to arrive through, and the hook asks
 * for exactly one room — the open card's board — and leaves it when the card closes.
 */
export const TASK_ITEM_EVENTS = ['item.updated', 'item.deleted', 'cell.changed'] as const;

export function isTaskItemEvent(event: Pick<EventEnvelope, 'type'>): boolean {
  return (TASK_ITEM_EVENTS as readonly string[]).includes(event.type);
}

/**
 * Bumped on every `task.changed`. Pass it to the page (and to the rail badge) as a dependency:
 * a re-read is one query, and the page already re-reads on every filter change.
 *
 * `cardBoardId` is the board of the item whose card is open over the page, or `null` when none
 * is (**P-319**). While it is set the hook holds that board's room and also bumps on the three
 * item events above; `joinBoard`/`leaveBoard` are reference-counted, so holding a room the board
 * screen may also hold in another tab is safe.
 */
export function useTasksRealtime(
  enabled = true,
  cardBoardId: string | null = null,
): { revision: number } {
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    if (!enabled || cardBoardId === null) return;
    try {
      void joinBoard(cardBoardId);
    } catch {
      // No socket: the card still opens and the list still re-reads when it closes.
      return;
    }
    return () => {
      try {
        leaveBoard(cardBoardId);
      } catch {
        // Already gone with the socket.
      }
    };
  }, [enabled, cardBoardId]);

  useEffect(() => {
    if (!enabled) return;
    let stop: (() => void) | undefined;
    try {
      stop = onEvent((event) => {
        if (isTaskEvent(event)) {
          setRevision((current) => current + 1);
          return;
        }
        /*
         * Narrowed to the open card's board on purpose: a person with several tabs open holds
         * other rooms through the same socket, and every cell written anywhere in them is not a
         * reason to re-read this list.
         */
        if (cardBoardId === null || !isTaskItemEvent(event)) return;
        if (String(event.boardId ?? '') !== cardBoardId) return;
        setRevision((current) => current + 1);
      });
    } catch {
      // No socket (a blocked websocket, a test renderer, the fixture harness): the page keeps
      // whatever it last read and the next fetch corrects it. Never a thrown error in the shell.
    }
    return () => stop?.();
  }, [enabled, cardBoardId]);

  return { revision };
}
